import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
} from 'react';
import DealerValues from '../apiService/dealerValues/dealerValuesApiService';

// ----------- Type Definitions -----------
interface DealerValuesContextType {
  loading: boolean;
  error: string;
  dealerValues: any[]; // List of dealer values
  selectedDealerValue: any | null; // Single dealer value details

  addDealerValue: (dealerValue: any) => Promise<void>;
  deleteDealerValue: (dealerValueId: string) => Promise<void>;
  editDealerValue: (dealerValueId: string, data: any) => Promise<void>;
  getDealerValue: (dealerValueId: string) => Promise<void>;
  getAllDealerValues: () => Promise<void>;
}

// ----------- Context Create -----------
export const DealerValuesContext = createContext<
  DealerValuesContextType | undefined
>(undefined);

// ----------- Provider Component -----------
export const DealerValuesProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [dealerValues, setDealerValues] = useState<any[]>([
    {
      _id: '1',
      name: 'Premium Dealer Value Package',
      description:
        'Exclusive dealer value package with premium benefits and services',
      image:
        'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop',
      value: 'Premium Value',
      category: 'Premium Package',
      validUntil: '2024-12-31',
      originalValue: '$10,000',
      discountedValue: '$8,500',
      benefits: [
        'Priority Customer Support',
        'Extended Warranty Coverage',
        'Premium Service Package',
        'Exclusive Dealer Network Access',
        'VIP Treatment',
        'Custom Financing Options',
      ],
      conditions: [
        'Valid until December 31, 2024',
        'Subject to dealer availability',
        'Cannot be combined with other offers',
        'Financing available through approved lenders',
        'Trade-in assessment required',
      ],
      dealerType: 'Premium',
      applicableModels: ['BMW X5', 'BMW X7', 'BMW 7 Series', 'BMW 8 Series'],
      isActive: true,
    },
    {
      _id: '2',
      name: 'Standard Dealer Value',
      description: 'Standard dealer value package with essential benefits',
      image:
        'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400&h=250&fit=crop',
      value: 'Standard Value',
      category: 'Standard Package',
      validUntil: '2024-12-25',
      originalValue: '$7,500',
      discountedValue: '$6,000',
      benefits: [
        'Basic Customer Support',
        'Standard Warranty',
        'Regular Service Package',
        'Dealer Network Access',
        'Standard Financing',
      ],
      conditions: [
        'Valid until December 25, 2024',
        'Subject to availability',
        'Standard terms apply',
        'Financing through approved lenders',
        'Trade-in required',
      ],
      dealerType: 'Standard',
      applicableModels: ['BMW 3 Series', 'BMW 5 Series', 'BMW X3', 'BMW X1'],
      isActive: true,
    },
    {
      _id: '3',
      name: 'Luxury Vehicle Valuation',
      description:
        'Premium pricing for high-end luxury vehicles with advanced features',
      image:
        'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&h=250&fit=crop',
      value: 'Luxury Value',
      category: 'Luxury Package',
      validUntil: '2024-12-28',
      originalValue: '$15,000',
      discountedValue: '$12,000',
      benefits: [
        'Concierge Service',
        'Premium Warranty',
        'Luxury Service Package',
        'Exclusive Network',
        'VIP Financing',
        'Personal Account Manager',
      ],
      conditions: [
        'Valid until December 28, 2024',
        'Luxury vehicle qualification required',
        'Premium terms apply',
        'Exclusive financing options',
        'Comprehensive assessment',
      ],
      dealerType: 'Luxury',
      applicableModels: ['BMW 7 Series', 'BMW 8 Series', 'BMW X7', 'BMW i7'],
      isActive: true,
    },
    {
      _id: '4',
      name: 'Electric Vehicle Pricing',
      description: 'Specialized pricing model for electric and hybrid vehicles',
      image:
        'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=400&h=250&fit=crop',
      value: 'EV Value',
      category: 'Electric Package',
      validUntil: '2024-12-30',
      originalValue: '$12,000',
      discountedValue: '$9,600',
      benefits: [
        'EV Specialist Support',
        'Battery Warranty',
        'Charging Infrastructure',
        'Green Financing',
        'Eco-Friendly Benefits',
        'Home Charging Setup',
      ],
      conditions: [
        'Valid until December 30, 2024',
        'Electric vehicle only',
        'Green financing available',
        'Charging setup included',
        'Environmental benefits apply',
      ],
      dealerType: 'Electric',
      applicableModels: ['BMW i3', 'BMW i4', 'BMW i7', 'BMW iX'],
      isActive: true,
    },
    {
      _id: '5',
      name: 'Sports Car Valuation',
      description:
        'Performance-based pricing for sports and high-performance vehicles',
      image:
        'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400&h=250&fit=crop',
      value: 'Sports Value',
      category: 'Sports Package',
      validUntil: '2024-12-20',
      originalValue: '$18,000',
      discountedValue: '$14,400',
      benefits: [
        'Performance Support',
        'Track Day Access',
        'Performance Warranty',
        'Racing Network',
        'Performance Financing',
        'Driver Training',
      ],
      conditions: [
        'Valid until December 20, 2024',
        'Sports vehicle qualification',
        'Performance terms apply',
        'Track access included',
        'Driver assessment required',
      ],
      dealerType: 'Sports',
      applicableModels: ['BMW M3', 'BMW M4', 'BMW M5', 'BMW M8'],
      isActive: true,
    },
    {
      _id: '6',
      name: 'Family Vehicle Pricing',
      description: 'Affordable pricing for family-oriented vehicles',
      image:
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
      value: 'Family Value',
      category: 'Family Package',
      validUntil: '2024-12-31',
      originalValue: '$6,000',
      discountedValue: '$4,800',
      benefits: [
        'Family Support',
        'Safety Features',
        'Family Service Package',
        'Flexible Financing',
        'Child Safety Setup',
        'Family Discounts',
      ],
      conditions: [
        'Valid until December 31, 2024',
        'Family vehicle qualification',
        'Flexible terms available',
        'Safety features included',
        'Family discounts apply',
      ],
      dealerType: 'Family',
      applicableModels: ['BMW X1', 'BMW X3', 'BMW 2 Series', 'BMW 3 Series'],
      isActive: true,
    },
  ]);
  const [selectedDealerValue, setSelectedDealerValue] = useState<any | null>(
    null,
  );

  // ----------- API Calls Using DealerValues Service -----------
  const addDealerValue = async (dealerValue: any) => {
    setLoading(true);
    setError('');
    try {
      await DealerValues.addDealerValue(dealerValue);
      await getAllDealerValues(); // Refresh list after adding
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to add dealer value',
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteDealerValue = async (dealerValueId: string) => {
    setLoading(true);
    setError('');
    try {
      await DealerValues.deleteDealerValue(dealerValueId);
      await getAllDealerValues(); // Refresh list after deletion
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete dealer value',
      );
    } finally {
      setLoading(false);
    }
  };

  const editDealerValue = async (dealerValueId: string, data: any) => {
    setLoading(true);
    setError('');
    try {
      await DealerValues.editDealerValue(dealerValueId, data);
      await getAllDealerValues(); // Refresh list after update
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to edit dealer value',
      );
    } finally {
      setLoading(false);
    }
  };

  const getDealerValue = async (dealerValueId: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await DealerValues.getDealerValue(dealerValueId);
      setSelectedDealerValue(response); // Store the single dealer value
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch dealer value details',
      );
    } finally {
      setLoading(false);
    }
  };

  const getAllDealerValues = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await DealerValues.getAllDealerValues();
      setDealerValues(Array.isArray(response.body) ? response.body : []);
    } catch (err: unknown) {
      // Fallback to mock data when API fails
      const mockDealerValues = [
        {
          _id: '1',
          name: 'Premium Dealer Value Package',
          description:
            'Exclusive dealer value package with premium benefits and services',
          image:
            'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop',
          value: 'Premium Value',
          category: 'Premium Package',
          validUntil: '2024-12-31',
          originalValue: '$10,000',
          discountedValue: '$8,500',
          benefits: [
            'Priority Customer Support',
            'Extended Warranty Coverage',
            'Premium Service Package',
            'Exclusive Dealer Network Access',
            'VIP Treatment',
            'Custom Financing Options',
          ],
          conditions: [
            'Valid until December 31, 2024',
            'Subject to dealer availability',
            'Cannot be combined with other offers',
            'Financing available through approved lenders',
            'Trade-in assessment required',
          ],
          dealerType: 'Premium',
          applicableModels: [
            'BMW X5',
            'BMW X7',
            'BMW 7 Series',
            'BMW 8 Series',
          ],
          isActive: true,
        },
        {
          _id: '2',
          name: 'Standard Dealer Value',
          description: 'Standard dealer value package with essential benefits',
          image:
            'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400&h=250&fit=crop',
          value: 'Standard Value',
          category: 'Standard Package',
          validUntil: '2024-12-25',
          originalValue: '$7,500',
          discountedValue: '$6,000',
          benefits: [
            'Basic Customer Support',
            'Standard Warranty',
            'Regular Service Package',
            'Dealer Network Access',
            'Standard Financing',
          ],
          conditions: [
            'Valid until December 25, 2024',
            'Subject to availability',
            'Standard terms apply',
            'Financing through approved lenders',
            'Trade-in required',
          ],
          dealerType: 'Standard',
          applicableModels: [
            'BMW 3 Series',
            'BMW 5 Series',
            'BMW X3',
            'BMW X1',
          ],
          isActive: true,
        },
        {
          _id: '3',
          name: 'Luxury Vehicle Valuation',
          description:
            'Premium pricing for high-end luxury vehicles with advanced features',
          image:
            'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&h=250&fit=crop',
          value: 'Luxury Value',
          category: 'Luxury Package',
          validUntil: '2024-12-28',
          originalValue: '$15,000',
          discountedValue: '$12,000',
          benefits: [
            'Concierge Service',
            'Premium Warranty',
            'Luxury Service Package',
            'Exclusive Network',
            'VIP Financing',
            'Personal Account Manager',
          ],
          conditions: [
            'Valid until December 28, 2024',
            'Luxury vehicle qualification required',
            'Premium terms apply',
            'Exclusive financing options',
            'Comprehensive assessment',
          ],
          dealerType: 'Luxury',
          applicableModels: [
            'BMW 7 Series',
            'BMW 8 Series',
            'BMW X7',
            'BMW i7',
          ],
          isActive: true,
        },
        {
          _id: '4',
          name: 'Electric Vehicle Pricing',
          description:
            'Specialized pricing model for electric and hybrid vehicles',
          image:
            'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=400&h=250&fit=crop',
          value: 'EV Value',
          category: 'Electric Package',
          validUntil: '2024-12-30',
          originalValue: '$12,000',
          discountedValue: '$9,600',
          benefits: [
            'EV Specialist Support',
            'Battery Warranty',
            'Charging Infrastructure',
            'Green Financing',
            'Eco-Friendly Benefits',
            'Home Charging Setup',
          ],
          conditions: [
            'Valid until December 30, 2024',
            'Electric vehicle only',
            'Green financing available',
            'Charging setup included',
            'Environmental benefits apply',
          ],
          dealerType: 'Electric',
          applicableModels: ['BMW i3', 'BMW i4', 'BMW i7', 'BMW iX'],
          isActive: true,
        },
        {
          _id: '5',
          name: 'Sports Car Valuation',
          description:
            'Performance-based pricing for sports and high-performance vehicles',
          image:
            'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400&h=250&fit=crop',
          value: 'Sports Value',
          category: 'Sports Package',
          validUntil: '2024-12-20',
          originalValue: '$18,000',
          discountedValue: '$14,400',
          benefits: [
            'Performance Support',
            'Track Day Access',
            'Performance Warranty',
            'Racing Network',
            'Performance Financing',
            'Driver Training',
          ],
          conditions: [
            'Valid until December 20, 2024',
            'Sports vehicle qualification',
            'Performance terms apply',
            'Track access included',
            'Driver assessment required',
          ],
          dealerType: 'Sports',
          applicableModels: ['BMW M3', 'BMW M4', 'BMW M5', 'BMW M8'],
          isActive: true,
        },
        {
          _id: '6',
          name: 'Family Vehicle Pricing',
          description: 'Affordable pricing for family-oriented vehicles',
          image:
            'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=250&fit=crop',
          value: 'Family Value',
          category: 'Family Package',
          validUntil: '2024-12-31',
          originalValue: '$6,000',
          discountedValue: '$4,800',
          benefits: [
            'Family Support',
            'Safety Features',
            'Family Service Package',
            'Flexible Financing',
            'Child Safety Setup',
            'Family Discounts',
          ],
          conditions: [
            'Valid until December 31, 2024',
            'Family vehicle qualification',
            'Flexible terms available',
            'Safety features included',
            'Family discounts apply',
          ],
          dealerType: 'Family',
          applicableModels: [
            'BMW X1',
            'BMW X3',
            'BMW 2 Series',
            'BMW 3 Series',
          ],
          isActive: true,
        },
      ];
      setDealerValues(mockDealerValues);
      setError('Using mock data - API not available');
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <DealerValuesContext.Provider
      value={{
        loading,
        error,
        dealerValues,
        selectedDealerValue,
        addDealerValue,
        deleteDealerValue,
        editDealerValue,
        getDealerValue,
        getAllDealerValues,
      }}
    >
      {children}
    </DealerValuesContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useDealerValues = (): DealerValuesContextType => {
  const context = useContext(DealerValuesContext);
  if (context === undefined) {
    throw new Error(
      'useDealerValues must be used within a DealerValuesProvider',
    );
  }
  return context;
};
